import BaseController from './base_controller'

const EXCLUDED_PROXY_ATTRIBUTES = ['data-action', 'data-calc-floating-bar-target', 'data-vessel-form-target', 'id', 'type']
const FLOATING_BAR_HIDE_BUFFER = 56

export default class extends BaseController {
  static targets = ['floatingBar', 'floatingButtons', 'formsWrapper', 'submitButton', 'tabPanel']

  connect() {
    this.buttonObservers = new Map()
    this.proxyButtonIds = new WeakMap()
    this.sourceButtonsById = new Map()
    this.sourceButtonIdCounter = 0
    this.rebuildFrame = null
    this.boundRebuild = () => this.queueRebuild()
    this.boundCollapseRebuild = () => this.queueRebuild()

    this.rebuildEvents = [
      'calculator:fleetChanged',
      'component-tab-group:tab-selected',
      'select-tab',
      'vessel-group:changed',
    ]

    this.rebuildEvents
      .forEach(event => window.addEventListener(event, this.boundRebuild))

    this.boundCheckVisibility = () => this.checkVisibility()
    window.addEventListener('scroll', this.boundCheckVisibility, { passive: true })
    this.element.addEventListener('show.bs.collapse', this.boundCollapseRebuild)
    this.element.addEventListener('hide.bs.collapse', this.boundCollapseRebuild)
    this.element.addEventListener('shown.bs.collapse', this.boundCollapseRebuild)
    this.element.addEventListener('hidden.bs.collapse', this.boundCollapseRebuild)

    this.queueRebuild()
  }

  disconnect() {
    this.rebuildEvents
      .forEach(event => window.removeEventListener(event, this.boundRebuild))
    window.removeEventListener('scroll', this.boundCheckVisibility)
    this.element.removeEventListener('show.bs.collapse', this.boundCollapseRebuild)
    this.element.removeEventListener('hide.bs.collapse', this.boundCollapseRebuild)
    this.element.removeEventListener('shown.bs.collapse', this.boundCollapseRebuild)
    this.element.removeEventListener('hidden.bs.collapse', this.boundCollapseRebuild)
    this.cancelQueuedRebuild()
    this.disconnectButtonObservers()
  }

  submitButtonTargetConnected() {
    this.queueRebuild()
  }

  submitButtonTargetDisconnected() {
    this.queueRebuild()
  }

  queueRebuild() {
    this.cancelQueuedRebuild()
    this.rebuildFrame = requestAnimationFrame(() => {
      this.rebuildFrame = null
      this.rebuild()
    })
  }

  cancelQueuedRebuild() {
    if (this.rebuildFrame) {
      cancelAnimationFrame(this.rebuildFrame)
      this.rebuildFrame = null
    }
  }

  rebuild() {
    if (!this.hasFloatingButtonsTarget) return

    this.disconnectButtonObservers()
    this.sourceButtonsById.clear()
    const container = this.floatingButtonsTarget
    container.innerHTML = ''
    const activeWrapper = this.activeFormsWrapper()

    if (activeWrapper && this.hasFloatingBarTarget) {
      this.floatingBarTarget.style.left = `${activeWrapper.getBoundingClientRect().left}px`
    }

    this.activeSubmitButtons().forEach(btn => {
      const slot = document.createElement('div')
      slot.className = 'calc-floating-slot'
      const proxy = document.createElement('button')
      this.prepareProxyButton(proxy, btn)
      this.observeSubmitButton(btn, proxy)
      slot.appendChild(proxy)
      container.appendChild(slot)
    })

    if (activeWrapper && this.hasFloatingBarTarget) this.floatingBarTarget.scrollLeft = activeWrapper.scrollLeft
    this.checkVisibility()
  }

  syncFloatingBar(event) {
    if (!this.hasFloatingBarTarget) return
    this.floatingBarTarget.scrollLeft = event.target.scrollLeft
  }

  syncFormsWrapper(event) {
    if (this._suppressingFormsSync) return
    const activeWrapper = this.activeFormsWrapper()
    if (activeWrapper) {
      activeWrapper.scrollLeft = event.target.scrollLeft
    }
  }

  suppressFormsSync() {
    this._suppressingFormsSync = true
    clearTimeout(this._suppressFormsSyncFallback)
    this._suppressFormsSyncFallback = setTimeout(() => { this._suppressingFormsSync = false }, 2000)

    const wrapper = this.activeFormsWrapper()
    if (wrapper) {
      const handler = () => {
        this._suppressingFormsSync = false
        clearTimeout(this._suppressFormsSyncFallback)
        wrapper.removeEventListener('scrollend', handler)
      }
      wrapper.addEventListener('scrollend', handler)
    }
  }

  activeFormsWrapper() {
    const activePanel = this.tabPanelTargets.find(panel => panel.classList.contains('is-active'))
    const activePanelId = activePanel?.dataset.panel

    if (activePanelId) {
      return this.formsWrapperTargets.find(wrapper => wrapper.dataset.panel === activePanelId)
    }

    return this.formsWrapperTargets[0]
  }

  activeSubmitButtons() {
    const activeWrapper = this.activeFormsWrapper()
    if (!activeWrapper) return []

    return this.submitButtonTargets.filter(button => activeWrapper.contains(button))
  }

  prepareProxyButton(proxy, sourceButton) {
    this.mirrorAttributes(sourceButton, proxy)
    proxy.type = 'button'
    proxy.innerHTML = sourceButton.innerHTML
    proxy.disabled = sourceButton.disabled
    proxy.classList.add('calc-floating-button')
    proxy.dataset.sourceButtonId = this.sourceButtonId(sourceButton)
    proxy.dataset.action = 'click->calc-floating-bar#submitVessel'
    this.syncLoadingState(proxy, sourceButton)
  }

  mirrorAttributes(source, target) {
    Array.from(target.attributes).forEach(attribute => {
      if (!EXCLUDED_PROXY_ATTRIBUTES.includes(attribute.name)) {
        target.removeAttribute(attribute.name)
      }
    })

    Array.from(source.attributes).forEach(attribute => {
      if (EXCLUDED_PROXY_ATTRIBUTES.includes(attribute.name)) return
      target.setAttribute(attribute.name, attribute.value)
    })
  }

  observeSubmitButton(button, proxy) {
    const observer = new MutationObserver(() => {
      if (!proxy.isConnected) return
      this.prepareProxyButton(proxy, button)
      this.checkVisibility()
    })
    observer.observe(button, {
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      childList: true,
      subtree: true,
    })
    this.buttonObservers.set(button, observer)
  }

  submitVessel(event) {
    event.preventDefault()
    const button = this.sourceButtonsById.get(event.currentTarget.dataset.sourceButtonId)
    if (!button || button.disabled) return
    button.click()
  }

  sourceButtonId(button) {
    if (!this.proxyButtonIds.has(button)) {
      this.sourceButtonIdCounter += 1
      this.proxyButtonIds.set(button, `calc-floating-source-${this.sourceButtonIdCounter}`)
    }
    const id = this.proxyButtonIds.get(button)
    this.sourceButtonsById.set(id, button)
    return id
  }

  disconnectButtonObservers() {
    this.buttonObservers?.forEach(observer => observer.disconnect())
    this.buttonObservers?.clear()
  }

  syncLoadingState(proxy, sourceButton) {
    const sourceIsSubmitting = sourceButton.classList.contains('is-submitting')
    proxy.classList.toggle('is-submitting', sourceIsSubmitting)
    const proxyIcon = proxy.querySelector('[data-ajax-spinner-target="icon"]')
    const proxySpinner = proxy.querySelector('[data-ajax-spinner-target="spinner"]')
    if (proxyIcon) proxyIcon.classList.toggle('d-none', sourceIsSubmitting)
    if (proxySpinner) proxySpinner.classList.toggle('d-none', !sourceIsSubmitting)
  }

  checkVisibility() {
    if (!this.hasFloatingBarTarget) return
    const anyVisible = this.activeSubmitButtons().some(btn => this.isSubmitButtonVisible(btn))
    this.floatingBarTarget.classList.toggle('is-visible', !anyVisible)
  }

  isSubmitButtonVisible(button) {
    const rect = button.getBoundingClientRect()
    const floatingBarHeight = this.floatingBarTarget.getBoundingClientRect().height
    const visibleBottom = window.innerHeight - floatingBarHeight + FLOATING_BAR_HIDE_BUFFER

    return rect.top < visibleBottom && rect.bottom > 0
  }
}
